import { router, usePage } from "@inertiajs/react";
import { conversationPath, messageRepliesPath } from "@travily/routes";
import type Message from "@travily/types/models/MessagesMessage";
import { useCallback, useMemo, useState } from "react";
import {
  MAX_ATTACHMENTS,
  type UseDirectUploadMultipleResult,
  useDirectUploadMultiple,
} from "@/hooks/use-direct-upload-multiple";
import type { SharedProps } from "@/types";

interface UseConversationSendOptions {
  /** The counterpart's handle — the address a first message uses when no conversation row exists. */
  accountHandle: string | null;
  /** Null until a first message has opened the conversation. */
  conversationSqid: string | null;
  /** Seeds the composer on mount; later values are ignored. */
  initialMessageValue?: string;
  /** The server-rendered thread. Undefined is MessageList's loading state. */
  messages: Message[] | undefined;
  /** Fires once the server has taken the message — not when it has answered with a refusal. */
  onSent?: () => void;
  /**
   * Which props the send's redirect-back has to bring home. Left open, the whole host page
   * re-renders — right for the messages page, which is the thread, and wrong for a corner panel
   * floating over someone else's page.
   */
  reloadProps?: string[];
}

interface UseConversationSendResult {
  isSending: boolean;
  messages: Message[] | undefined;
  messageValue: string;
  send: () => void;
  setMessageValue: (value: string) => void;
  uploads: UseDirectUploadMultipleResult;
}

/**
 * A conversation's composer — draft, attachment uploads and an optimistic send — shared by the
 * messages page and the message dock.
 *
 * The send addresses whichever exists: a reply into `conversationSqid`, or, before any conversation
 * row does, a create posted at the counterpart's handle. Both carry the same body and signed ids —
 * the two endpoints validate them through one shared contract — so a first message is a whole
 * message, pictures included.
 */
export function useConversationSend({
  accountHandle,
  conversationSqid,
  initialMessageValue = "",
  messages,
  onSent,
  reloadProps,
}: UseConversationSendOptions): UseConversationSendResult {
  const { current_user } = usePage<SharedProps>().props;
  const [messageValue, setMessageValue] = useState(initialMessageValue);
  const [isSending, setIsSending] = useState(false);
  // Locally-appended sent messages shown instantly; cleared once the server
  // round-trip (and its reloaded `messages` prop) reconciles them.
  const [pendingMessages, setPendingMessages] = useState<Message[]>([]);
  const uploads = useDirectUploadMultiple(MAX_ATTACHMENTS);

  const allMessages = useMemo(() => {
    // An optimistic copy ends the loading state even before the thread has loaded.
    if (messages === undefined && pendingMessages.length === 0) return undefined;
    return [...(messages ?? []), ...pendingMessages];
  }, [messages, pendingMessages]);

  const send = useCallback(() => {
    const body = messageValue.trim();
    const signedIds = uploads.completedSignedIds;
    // A picture on its own is a message.
    if ((!body && signedIds.length === 0) || !current_user) return;
    if (!conversationSqid && !accountHandle) return;
    setIsSending(true);

    const optimisticMessage: Message = {
      id: -Date.now(),
      body,
      created_at: new Date().toISOString(),
      sender_id: current_user.id,
      sender_name: current_user.name,
      sender_initial: current_user.initial,
      sender_avatar: current_user.avatar,
      // The optimistic copy shows the words instantly; the images appear once the
      // server round-trip returns their variants.
      attachments: [],
    };
    setPendingMessages((prev) => [...prev, optimisticMessage]);
    setMessageValue("");

    // Drop the phantom optimistic copy and hand the words back, so the user can retry instead of
    // silently losing the message. The uploads are deliberately kept: the pictures were rejected
    // with the message, not instead of it.
    const restoreDraft = () => {
      setPendingMessages((prev) => prev.filter((message) => message.id !== optimisticMessage.id));
      setMessageValue((current) => current || body);
    };

    const options = {
      preserveScroll: true,
      preserveState: true,
      only: reloadProps,
      onSuccess: (page: { props: SharedProps }) => {
        // A rejected send is a redirect carrying an alert, not an `errors` payload, so Inertia
        // calls this the success path. An alert here means nothing was sent.
        if (page.props.flash?.alert) return restoreDraft();

        // Server reload now carries the persisted message; drop the optimistic copy.
        setPendingMessages([]);
        uploads.reset();
        onSent?.();
      },
      // Transport or 5xx failure.
      onError: restoreDraft,
      onFinish: () => setIsSending(false),
    };

    if (conversationSqid) {
      router.post(messageRepliesPath(conversationSqid), { body, signed_ids: signedIds }, options);
    } else {
      router.post(conversationPath(), { account_handle: accountHandle, body, signed_ids: signedIds }, options);
    }
  }, [
    accountHandle,
    conversationSqid,
    current_user,
    messageValue,
    onSent,
    reloadProps,
    uploads.completedSignedIds,
    uploads.reset,
  ]);

  return { isSending, messages: allMessages, messageValue, send, setMessageValue, uploads };
}
